import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  inject,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { ButtonModule } from '@openng/optimus-ui/button';

export interface CustomDialogOption<T = unknown> {
  title?: string | TemplateRef<unknown>;
  content: string | TemplateRef<unknown>;
  contentData?: T;
  buttons?: CustomButtonOption<T>[];
}

export type CustomButtonType = 'ok' | 'cancel' | 'command';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface CustomButtonOption<T = any> {
  type?: CustomButtonType;
  text?: string;
  command?: (data: T, options: CommandOptions) => Promise<boolean>;
  disabled?: (data: T) => boolean;
}

export interface CustomButtonTemplate<T = unknown> {
  template: string;
  option: CustomButtonOption<T>;
}

@Component({
  selector: 'core-custom-dialog',
  imports: [CommonModule, MatDialogModule, ButtonModule],
  template: `<ng-container>
      <div class="p-4">
        @if (title || titleTemplate) {
          <div data-testid="customTitle" mat-dialog-title>
            @if (titleTemplate) {
              <ng-container
                [ngTemplateOutlet]="titleTemplate"
                [ngTemplateOutletContext]="{ option: option }"
              >
              </ng-container>
            }
            @if (title) {
              {{ title }}
            }
          </div>
        }
        <div data-testid="customContent" mat-dialog-content>
          <ng-container>
            @if (contentTemplate) {
              <ng-container
                [ngTemplateOutlet]="contentTemplate"
                [ngTemplateOutletContext]="{
                  option: option,
                  data: option.contentData ?? null,
                  dialog: dialogRef,
                }"
              >
              </ng-container>
            }
            @if (content) {
              {{ content }}
            }
          </ng-container>
        </div>
        <div
          class="flex justify-end"
          data-testid="customButtons"
          mat-dialog-actions
        >
          @for (buttonTemplate of buttonTemplates; track buttonTemplate) {
            @switch (buttonTemplate.template) {
              @case ('command') {
                <ng-container
                  [ngTemplateOutlet]="command"
                  [ngTemplateOutletContext]="{
                    data: option.contentData,
                    option: buttonTemplate.option,
                  }"
                >
                </ng-container>
              }
              @case ('ok') {
                <ng-container
                  [ngTemplateOutlet]="ok"
                  [ngTemplateOutletContext]="{ option: buttonTemplate.option }"
                ></ng-container>
              }
              @case ('cancel') {
                <ng-container
                  [ngTemplateOutlet]="cancel"
                  [ngTemplateOutletContext]="{ option: buttonTemplate.option }"
                ></ng-container>
              }
            }
          }
        </div>
      </div>
    </ng-container>
    <ng-template #ok let-option="option">
      <p-button
        class="text-sm"
        (click)="onOk()"
        data-testid="btnOK"
        [label]="option.text ?? defaultOkLabel"
      />
    </ng-template>
    <ng-template #cancel let-option="option">
      <p-button
        class="text-sm"
        (click)="onCancel()"
        data-testid="btnCancel"
        severity="secondary"
        [outlined]="true"
        [label]="option.text ?? defaultCancelLabel"
        cdkFocusInitial
      />
    </ng-template>
    <ng-template #command let-option="option" let-data="data">
      <p-button
        [disabled]="$safeNavigationMigration(option.disabled?.(data))"
        (click)="onCommand(option, data)"
        data-testid="btnCommand"
        [label]="option.text"
      />
    </ng-template>`,
  styles: [
    `
      .mdc-dialog__title {
        @apply pl-2 pt-0;
      }
      .mdc-dialog__content {
        @apply pl-4;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomDialogComponent {
  protected readonly dialogRef =
    inject<MatDialogRef<CustomDialogComponent>>(MatDialogRef);
  protected option = inject<CustomDialogOption<unknown>>(MAT_DIALOG_DATA);

  protected readonly defaultOkLabel = $localize`:@@alert-dialog-btn-ok:OK`;
  protected readonly defaultCancelLabel = $localize`:@@alert-dialog-btn-cancel:Cancel`;

  protected title?: string;
  protected titleTemplate?: TemplateRef<unknown>;
  protected content?: string;
  protected contentTemplate?: TemplateRef<unknown>;
  protected buttonTemplates: CustomButtonTemplate<unknown>[];

  public constructor() {
    const dialogRef = this.dialogRef;
    const option = this.option;

    dialogRef.disableClose = true;
    if (option.title && typeof option.title == 'string') {
      this.title = option.title;
    } else if (typeof option.title == 'object') {
      this.titleTemplate = option.title;
    }
    if (option.content && typeof option.content == 'string') {
      this.content = option.content;
    } else if (typeof option.content == 'object') {
      this.contentTemplate = option.content;
    }
    if (option.buttons) {
      this.buttonTemplates = option.buttons.map<CustomButtonTemplate<unknown>>(
        btn => ({
          template: btn.type ?? 'ok',
          option: btn,
        }),
      );
    } else {
      this.buttonTemplates = [
        {
          template: 'ok',
          option: {},
        },
      ];
    }
  }

  protected onOk() {
    this.dialogRef.close(true);
  }

  protected onCancel() {
    this.dialogRef.close(false);
  }

  protected async onCommand(option: CustomButtonOption, data: unknown) {
    if (option.command) {
      const options: CommandOptions = { autoClose: true };
      const result = await option.command(data, options);
      if (options.autoClose) this.dialogRef.close(result);
    } else {
      this.dialogRef.close(true);
    }
  }
}

export interface CommandOptions {
  autoClose: boolean;
}
